import React, { useState, useEffect } from "react";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import ActivityLogDetailsModal from "../components/modals/ActivityLogDetailsModal";
import { ActionTypeIcon, ActivityLogEyeIcon, Warning } from "../components/icons";
import { useGetAllActivityLogsQuery, useLazyGetAllActivityLogsQuery, useGetUsersQuery, useCleanupActivityLogsMutation } from "../store/api";
import toast from "react-hot-toast";
import Modal from "../components/modals/Modal";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";

export default function ActivityLog() {
  // Defaulting to true for now so you can see it, as the exact admin role string is unknown
  const isAdmin = true;

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activityType, setActivityType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [cleanupActivityLogs, { isLoading: isDeleting }] = useCleanupActivityLogsMutation();

  const handleDeleteOldLogs = async () => {
    try {
      const res = await cleanupActivityLogs().unwrap();
      const count = res?.data?.deletedCount !== undefined ? res.data.deletedCount : res?.deletedCount || 0;
      toast.success(`${res.message || "Old activity logs deleted successfully."} (${count} logs removed)`);
      setIsDeleteModalOpen(false);
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete old logs.");
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activityType, startDate, endDate, limit]);

  const [triggerGetAllLogs] = useLazyGetAllActivityLogsQuery();

  const { data, isLoading, isFetching, refetch } = useGetAllActivityLogsQuery({
    page,
    limit,
    search: debouncedSearch,
    activityType,
    startDate,
    endDate,
  });
  const { data: usersPage1Data } = useGetUsersQuery({ page: 1 });
  const { data: usersPage2Data } = useGetUsersQuery({ page: 2 });
  const { data: usersPage3Data } = useGetUsersQuery({ page: 3 });
  const columns = [
    { key: "time", label: "Time Stamp", className: "col-span-2 text-center" },
    {
      key: "user",
      label: "User",
      className: "col-span-3 text-center",
    },
    {
      key: "action-type",
      label: "Action Type",
      className: "col-span-3 text-center",
    },
    { key: "description", label: "Description", className: "col-span-3" },

    { key: "action", label: "Action", className: "col-span-1 text-right pr-6" },
  ];

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(date);
  };

  const allUsers = [
    ...(usersPage1Data?.data?.users || []),
    ...(usersPage2Data?.data?.users || []),
    ...(usersPage3Data?.data?.users || []),
  ];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await triggerGetAllLogs({
        page: 1,
        limit: limit,
        search,
        activityType,
        startDate,
        endDate,
      }).unwrap();

      const logsToExport = response?.data?.logs || response?.data || [];
      if (logsToExport.length === 0) {
        toast.error("No logs to export!");
        setIsExporting(false);
        return;
      }

      const doc = new jsPDF("landscape");
      doc.setFontSize(18);
      doc.text("System Audit Logs Report", 14, 22);

      const tableColumn = ["Time Stamp", "User", "Email", "Action Type", "Description", "IP Address"];
      const tableRows = logsToExport.map((log) => {
        const logUserId = log?.userId || log?._userId || log?.user_id;
        const matchingUser = allUsers.find((user) => user?._id === logUserId);

        return [
          formatTimestamp(log.createdAt),
          matchingUser?.name || log?.user?.name || "Unknown User",
          matchingUser?.email || log?.user?.email || "N/A",
          log.activityType,
          log.actionDescription,
          log.ipAddress || "N/A"
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        theme: "striped",
      });

      doc.save(`Activity_Logs_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF generated successfully");

    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export logs");
    } finally {
      setIsExporting(false);
    }
  };

  const rows = (data?.data?.logs || data?.data || []).map((log) => {
    const logUserId = log?.userId || log?._userId || log?.user_id;
    const matchingUser = allUsers.find((user) => user?._id === logUserId);

    return {
      ...log,
      userName: matchingUser?.name || log?.user?.name || "Unknown User",
      userEmail: matchingUser?.email || log?.user?.email || "",
    };
  });
  const pagination = data?.data?.pagination || data?.pagination;

  const renderRow = (r) => (
    <div key={r._id} className="bg-[#EEEEEE40] ">
      <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] ">
        <div className="col-span-3  ">
          <p className="text-[#4B5563] text-sm">
            {" "}
            {formatTimestamp(r.createdAt)}
          </p>
        </div>

        <div className="col-span-3 text-sm">
          <p className="font-medium text-black">{r.userName}</p>
          <p className="text-[#6B7280]">{r.userEmail}</p>
        </div>

        <div className="col-span-2">
          <div
            className={` ${r.activityType === "USER" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FCE3DC] text-[#B30606]"} text-xs font-medium rounded-full py-1 px-2.5 w-fit  flex items-center gap-1`}
          >
            <ActionTypeIcon
              color={r.activityType === "USER" ? "#166534" : "#B30606"}
            />{" "}
            <p>{r.activityType}</p>
          </div>
        </div>

        <div className="col-span-3">
          <p className="text-sm text-[#4B5563]">{r.actionDescription}</p>
        </div>

        <div className="col-span-1 flex justify-center ">
          <button
            aria-label="view-details"
            onClick={() => {
              setSelectedLog({
                logId: r._id,
                time: formatTimestamp(r.createdAt),
                name: r.userName,
                email: r.userEmail,
                actionType: r.activityType,
                ipAddress: r.ipAddress,
                location: r.location,
                desc: r.actionDescription,
              });
              setDetailsOpen(true);
            }}
            className="cursor-pointer hover:opacity-80 transition"
          >
            <ActivityLogEyeIcon />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-6 md:pt-14 px-4 md:pl-8 md:pr-12 min-h-screen">
      <HeaderInfo
        title="System Audit Logs"
        desc="Real-time tracking of administrative operations and access"
        isSearchBar={true}
        searchPlaceholder="Query by action description, route, IP address, or location"
        searchValue={search}
        onSearchChange={setSearch}
        onFilterClick={() => setShowFilters(!showFilters)}
        hasSearchText={showFilters || !!activityType || !!startDate || !!endDate}
        extraButtons={
          isAdmin && (
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="rounded-[10px] text-sm lg:text-base bg-[#DC2626] hover:bg-[#b91c1c] text-white flex items-center gap-1.5 lg:gap-2.5 py-2 lg:py-2.5 px-6 lg:px-[30.5px] transition-colors"
            >
              Cleanup Old Logs
            </button>
          )
        }
      />

      {showFilters && (
        <div className="mt-4 ml-0 md:ml-2.5 flex flex-wrap items-center gap-4 bg-[#EEEEEE40] p-4 rounded-[10px] border border-[#D9D9D9]">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Activity Type</label>
            <select
              className="bg-white border border-[#D9D9D9] rounded-md px-3 py-2 text-sm outline-none text-black"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="AUTH">AUTH</option>
              <option value="USER">USER</option>
              <option value="ROLE">ROLE</option>
              <option value="INVENTORY">INVENTORY</option>
              <option value="REQUEST">REQUEST</option>
              <option value="SYSTEM">SYSTEM</option>
              <option value="REPORT">REPORT</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Start Date</label>
            <input
              type="date"
              className="bg-white border border-[#D9D9D9] rounded-md px-3 py-2 text-sm outline-none text-black"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">End Date</label>
            <input
              type="date"
              className="bg-white border border-[#D9D9D9] rounded-md px-3 py-2 text-sm outline-none text-black"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Items</label>
            <select
              className="bg-white border border-[#D9D9D9] rounded-md px-3 py-2 text-sm outline-none text-black"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="1000">1000</option>
            </select>
          </div>
          <div className="flex items-end h-full mt-5 gap-3 ml-auto">
            <button
              onClick={() => {
                setActivityType("");
                setStartDate("");
                setEndDate("");
                setLimit(10);
              }}
              className="text-sm text-[#6D4E8A] underline mr-2"
            >
              Clear Filters
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="text-sm bg-[#279B9F] hover:bg-[#1f7a7d] text-white px-4 py-2 rounded-md disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {isExporting ? (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <Download size={16} />
              )}
              <span>{isExporting ? "Generating..." : "Export to PDF"}</span>
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 ml-0 md:ml-2.5 relative">
        <DataTable
          columns={columns}
          rows={rows}
          renderRow={renderRow}
          page={page}
          totalPages={pagination?.totalPages || 1}
          onNext={() => setPage((p) => p + 1)}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          isLoading={isLoading || isFetching}
        />
      </div>

      <ActivityLogDetailsModal
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedLog(null);
        }}
        logData={selectedLog || {}}
      />

      <Modal
        open={isDeleteModalOpen}
        onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
        title={null}
        panelClassName="max-w-[448px] !min-h-[218px] rounded-xl"
        centered={false}
      >
        <div className="w-full">
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-full h-12 w-12 bg-[#FEE2E2] flex items-center justify-center flex-shrink-0">
              <Warning />
            </div>
            <div>
              <p className="text-lg text-[#111827] font-bold">Delete Old Logs</p>
              <p className="text-sm text-[#4B5563]">This action cannot be undone</p>
            </div>
          </div>
          <p className="text-sm text-[#374151] leading-10">
            Are you sure you want to delete all activity logs older than 7 days? This action cannot be undone.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
              className="text-[#374151] border border-[#E5E7EB] py-2 font-medium text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteOldLogs}
              disabled={isDeleting}
              className="bg-[#DC2626] hover:bg-[#b91c1c] text-white py-2 font-medium text-sm rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
